import { ReactNode, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, useTrackingCode } from '@/contexts/GlobalProvider';
import Loading from '@/components/ui/Loading';

// 白名单：不需要登录的路径
const PUBLIC_PATHS = [
  '/login',
  '/register',
  '/_error',
  '/404',
  '/customer-service',
  '/room-closed',
  '/pg-verification',
];

/**
 * 检查路径是否在白名单中
 */
function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.some(path =>
    pathname === path || pathname.startsWith(path + '/')
  );
}

interface LayoutProps {
  children: ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const { isAuthenticated, loading, userInfo } = useAuth();
  const { initialized } = useTrackingCode();

  // 认证检查：非白名单页面需要登录
  useEffect(() => {
    // 白名单页面跳过
    if (isPublicPath(pathname)) return;
    // 加载中跳过
    if (loading) return;
    // 客户端未初始化跳过
    if (!initialized) return;

    if (!isAuthenticated) {
      const returnUrl = encodeURIComponent(pathname);
      navigate(`/login?redirect=${returnUrl}`, { replace: true });
    }
  }, [isAuthenticated, loading, pathname, initialized, navigate]);

  // 白名单页面直接渲染
  if (isPublicPath(pathname)) {
    return (
      <div className="min-h-screen bg-bg-page flex items-start justify-center">
        <div className="w-full max-w-[460px] min-h-screen bg-bg-page shadow-2xl">
          {children}
        </div>
      </div>
    );
  }

  // 加载中或未认证：显示 Loading
  if (loading || !isAuthenticated || !userInfo) {
    return (
      <div className="min-h-screen bg-bg-page flex items-center justify-center">
        <Loading fullscreen text="加载中..." />
      </div>
    );
  }

  // 已认证：渲染页面
  return (
    <div className="min-h-screen bg-bg-page flex items-start justify-center">
      <div className="w-full max-w-[460px] min-h-screen bg-bg-page shadow-2xl">
        {children}
      </div>
    </div>
  );
}
